import React, { useState, useEffect } from "react";
import { getAuth, signOut } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import app, { programHeadAuth } from "../../firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isCalendarDateInRange } from "../../utils/announcementDates";

import HeadCurriculum from './HeadCurriculum';
import SchedulingManagement from './SchedulingManagement';
import GESchedulingManagement from './GESchedulingManagement';
import ResetPassword from '../admin/users/ResetPassword';
const auth = programHeadAuth;
const db = getFirestore(app);

function ProgramHeadDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // User info
  const [department, setDepartment] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [allDepartments, setAllDepartments] = useState([]);
  const [phName, setPhName] = useState("");
  const [loading, setLoading] = useState(true);
  const [phEmail, setPhEmail] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [dashboardStudents, setDashboardStudents] = useState([]);
  const [dashboardStudentsLoading, setDashboardStudentsLoading] = useState(true);
  const [programSchedules, setProgramSchedules] = useState([]);
  const [programSchedulesLoading, setProgramSchedulesLoading] = useState(true);
  const [academicCalendar, setAcademicCalendar] = useState(null);
  const [dashboardNow, setDashboardNow] = useState(() => new Date());

  // Navigation
  const initialPage = searchParams.get("page") || "menu";
  const [page, setPage] = useState(initialPage);

  // Profile / header
  const profilePicUrl = "https://ui-avatars.com/api/?name=" + (phName || "Program Head");

  // Update page when URL query parameters change
  useEffect(() => {
    const newPage = searchParams.get("page") || "menu";
    setPage(newPage);
  }, [searchParams]);

  // Auth state listener - maintain session and redirect if not authenticated
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate("/academics");
      }
    });
    return unsubscribe;
  }, [navigate]);

  // Fetch program head info
  useEffect(() => {
    setLoading(true);
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setDepartment(data.department || "");
          setPhName(data.name || "");
          setPhEmail(data.email || user.email || "");
          if ((data.department || "").toLowerCase().includes("general")) {
            const depSnap = await getDocs(collection(db, "departments"));
            const deps = depSnap.docs.map(doc => doc.data().name).filter(Boolean);
            setAllDepartments(deps);
            setSelectedDepartment(deps[0] || "");
          } else {
            setSelectedDepartment(data.department || "");
          }
        } else {
          console.warn(`No user document found for uid=${user.uid}. Falling back to email lookup.`);
          const q = query(collection(db, "users"), where("email", "==", user.email));
          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
            const data = snapshot.docs[0].data();
            setDepartment(data.department || "");
            setPhName(data.name || "");
            setPhEmail(data.email || user.email || "");
            if ((data.department || "").toLowerCase().includes("general")) {
              const depSnap = await getDocs(collection(db, "departments"));
              const deps = depSnap.docs.map(doc => doc.data().name).filter(Boolean);
              setAllDepartments(deps);
              setSelectedDepartment(deps[0] || "");
            } else {
              setSelectedDepartment(data.department || "");
            }
          } else {
            console.warn("No user document found by email either.");
          }
        }
      } catch (err) {
        console.error("Failed to load program head data:", err);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const studentsUnsubscribe = onSnapshot(collection(db, "students"), snapshot => {
      setDashboardStudents(snapshot.docs.map(studentDoc => ({ id: studentDoc.id, ...studentDoc.data() })));
      setDashboardStudentsLoading(false);
    }, error => {
      console.error("Error loading Program Head dashboard students:", error);
      setDashboardStudents([]);
      setDashboardStudentsLoading(false);
    });
    const calendarUnsubscribe = onSnapshot(doc(db, "systemSettings", "announcement"), snapshot => {
      setAcademicCalendar(snapshot.exists() ? snapshot.data() : null);
    }, error => {
      console.error("Error loading academic term:", error);
      setAcademicCalendar(null);
    });
    const clockInterval = window.setInterval(() => setDashboardNow(new Date()), 60000);

    return () => {
      studentsUnsubscribe();
      calendarUnsubscribe();
      window.clearInterval(clockInterval);
    };
  }, []);

  const analyticsDepartment = selectedDepartment || department;
  useEffect(() => {
    if (!analyticsDepartment) {
      setProgramSchedules([]);
      setProgramSchedulesLoading(false);
      return;
    }
    setProgramSchedulesLoading(true);
    const scheduleQuery = query(
      collection(db, "schedules"),
      where("department", "==", analyticsDepartment)
    );
    const unsubscribe = onSnapshot(scheduleQuery, snapshot => {
      setProgramSchedules(snapshot.docs.map(scheduleDoc => ({ id: scheduleDoc.id, ...scheduleDoc.data() })));
      setProgramSchedulesLoading(false);
    }, error => {
      console.error("Error loading Program Head schedules:", error);
      setProgramSchedules([]);
      setProgramSchedulesLoading(false);
    });
    return unsubscribe;
  }, [analyticsDepartment]);

  const departmentStudents = dashboardStudents.filter(student =>
    (student.program || student.department) === analyticsDepartment
  );
  const activeAcademicTerm = [
    {
      term: "First Semester",
      schoolYearStart: academicCalendar?.firstSemYearStart,
      schoolYearEnd: academicCalendar?.firstSemYearEnd,
      start: academicCalendar?.firstSemStart,
      end: academicCalendar?.firstSemEnd
    },
    {
      term: "Second Semester",
      schoolYearStart: academicCalendar?.secondSemYearStart,
      schoolYearEnd: academicCalendar?.secondSemYearEnd,
      start: academicCalendar?.secondSemStart,
      end: academicCalendar?.secondSemEnd
    }
  ].find(semester => {
    return isCalendarDateInRange(semester.start, semester.end, dashboardNow);
  });
  const activeTermKey = activeAcademicTerm?.schoolYearStart && activeAcademicTerm?.schoolYearEnd
    ? `${activeAcademicTerm.schoolYearStart}-${activeAcademicTerm.schoolYearEnd}_${activeAcademicTerm.term}`
    : "";
  const activeTermLabel = activeAcademicTerm
    ? `AY ${activeAcademicTerm.schoolYearStart}-${activeAcademicTerm.schoolYearEnd} · ${activeAcademicTerm.term}`
    : "No active term";
  const totalStudents = departmentStudents.length;
  const newStudents = departmentStudents.filter(student =>
    String(student.category || "").toLowerCase() === "new student"
  ).length;
  const pendingStudents = departmentStudents.filter(student =>
    String(student.registrationStatus || "").toLowerCase() === "submitted"
  ).length;

  const yearLevelCounts = new Map();
  departmentStudents.forEach(student => {
    const termSchedules = activeTermKey && Array.isArray(student.termSchedules?.[activeTermKey])
      ? student.termSchedules[activeTermKey]
      : [];
    const scheduleYearLevels = [...new Set(termSchedules
      .map(schedule => schedule.yearLevel)
      .filter(Boolean))];
    const yearLevels = scheduleYearLevels.length > 0
      ? scheduleYearLevels
      : [student.yearLevel || student.preRegistration?.entryLevel].filter(Boolean);

    yearLevels.forEach(yearLevel => {
      const label = String(yearLevel);
      yearLevelCounts.set(label, (yearLevelCounts.get(label) || 0) + 1);
    });

  });
  const yearLevelStats = [...yearLevelCounts.entries()].sort((a, b) => {
    const firstYearNumber = Number(String(a[0]).match(/\d+/)?.[0] || Infinity);
    const secondYearNumber = Number(String(b[0]).match(/\d+/)?.[0] || Infinity);
    return firstYearNumber - secondYearNumber || a[0].localeCompare(b[0]);
  });
  const programSectionGroups = new Map();
  programSchedules.forEach(schedule => {
    if (!schedule.schoolYear || !schedule.semester || !schedule.yearLevel || !schedule.section) return;
    const key = [schedule.schoolYear, schedule.semester, schedule.yearLevel, schedule.section].join("||");
    if (!programSectionGroups.has(key)) {
      programSectionGroups.set(key, {
        schoolYear: schedule.schoolYear,
        semester: schedule.semester,
        yearLevel: schedule.yearLevel,
        section: schedule.section
      });
    }
  });
  const sectionStats = [...programSectionGroups.values()]
    .map(group => {
      const studentIds = new Set();
      const termKey = `${group.schoolYear}_${group.semester}`;
      departmentStudents.forEach(student => {
        const schedules = student.termSchedules?.[termKey];
        if (Array.isArray(schedules) && schedules.some(schedule =>
          schedule.schoolYear === group.schoolYear &&
          schedule.semester === group.semester &&
          schedule.yearLevel === group.yearLevel &&
          schedule.section === group.section
        )) {
          studentIds.add(student.id);
        }
      });
      return { ...group, students: studentIds.size };
    })
    .sort((a, b) => a.schoolYear.localeCompare(b.schoolYear) ||
      a.semester.localeCompare(b.semester) ||
      a.yearLevel.localeCompare(b.yearLevel) ||
      a.section.localeCompare(b.section));

  const enrollmentTrendByTerm = new Map();
  departmentStudents.forEach(student => {
    Object.entries(student.termSchedules || {}).forEach(([termKey, schedules]) => {
      if (!Array.isArray(schedules) || schedules.length === 0) return;
      if (!enrollmentTrendByTerm.has(termKey)) enrollmentTrendByTerm.set(termKey, new Set());
      enrollmentTrendByTerm.get(termKey).add(student.id);
    });
  });
  const enrollmentTrend = [...enrollmentTrendByTerm.entries()]
    .map(([termKey, studentIds]) => ({ termKey, students: studentIds.size }))
    .sort((a, b) => {
      const firstYear = Number(a.termKey.match(/\d{4}/)?.[0] || 0);
      const secondYear = Number(b.termKey.match(/\d{4}/)?.[0] || 0);
      if (firstYear !== secondYear) return firstYear - secondYear;
      const firstSemester = a.termKey.toLowerCase().includes("second") ? 1 : 0;
      const secondSemester = b.termKey.toLowerCase().includes("second") ? 1 : 0;
      return firstSemester - secondSemester;
    })
    .slice(-8);
  const maximumTrendCount = Math.max(1, ...enrollmentTrend.map(term => term.students));
  const maximumYearLevelCount = Math.max(1, ...yearLevelStats.map(([, count]) => count));
  const maximumSectionCount = Math.max(1, ...sectionStats.map(section => section.students));

  const handleEditSubject = (id) => {
    alert("Edit functionality has been moved to the Curriculum page.");
  };
  const handleDeleteSubject = (id) => {
    alert("Delete functionality has been moved to the Curriculum page.");
  };

  // Logout handler
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/academics");
  };

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", fontSize: 18, color: "#666" }}>Loading...</div>;

  // --- Menu Page ---
  if (page === "menu") {
    return (
      <div className="program-head-layout" style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
        {/* Sidebar */}
        <div className="program-head-sidebar" style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
          <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
          
          <button
            onClick={() => setPage("menu")}
            style={{
              background: page === "menu" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "menu" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Home
          </button>

          <button
            onClick={() => setPage("curriculum-flowchart")}
            style={{
              background: page === "curriculum-flowchart" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "curriculum-flowchart" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Curriculum Flowchart
          </button>

          <button
            onClick={() => setPage("scheduling")}
            style={{
              background: page === "scheduling" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "scheduling" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Scheduling
          </button>

          <button
            onClick={() => navigate("/program-head/student-course-lineup")}
            style={{
              background: "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              transition: "background 0.2s, color 0.2s"
            }}
          >
          Course Line-up
          </button>

          <div style={{ flex: 1 }} />
        </div>

        {/* Main area */}
        <div className="program-head-main" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          {/* Header */}
          <div className="program-head-header" style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "relative", zIndex: 10, justifyContent: "space-between" }}>
            <h1 style={{ margin: "0", fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Program Head Dashboard</h1>
            
            <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
              <span style={{ marginRight: 16, fontWeight: 600, fontSize: 18, color: "#155c2b" }}>{phName || "Program Head"}</span>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src={profilePicUrl}
                  alt="Profile"
                  style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e67e22" }}
                />
              </button>
              
              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: 8,
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  zIndex: 20,
                  minWidth: 200,
                  overflow: "hidden"
                }}>
                  <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>
                    {phName || "Program Head"}
                  </div>
                      <button
                        onClick={() => { setDropdownOpen(false); setShowResetModal(true); }}
                        style={{ width: "100%", padding: "12px 16px", background: "transparent", border: "none", color: "#155c2b", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", transition: "background 0.2s" }}
                        onMouseEnter={(e) => e.target.style.background = "#f5fff0"}
                        onMouseLeave={(e) => e.target.style.background = "transparent"}
                      >
                        Change Password
                      </button>
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      handleLogout();
                    }}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      background: "transparent",
                      border: "none",
                      color: "#e67e22",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "background 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#fef5f0"}
                    onMouseLeave={(e) => e.target.style.background = "transparent"}
                  >
                    Log out
                  </button>
                  {showResetModal && <ResetPassword email={phEmail} onClose={() => setShowResetModal(false)} />}
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="program-head-content" style={{ flex: 1, padding: 40, display: "flex", flexDirection: "column", overflowY: "auto" }}>
            <section style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 24, padding: 28, marginBottom: 28, background: "#155c2b", color: "#fff", borderRadius: 10 }}>
              <div>
                <div style={{ marginBottom: 8, color: "#c9e6d1", fontSize: 13, fontWeight: 700, textTransform: "uppercase" }}>Program Head Dashboard</div>
                <h2 style={{ margin: "0 0 10px", fontSize: 30 }}>Welcome, {phName || "Program Head"}</h2>
                <div style={{ fontSize: 16 }}>Department: {department || "Loading department..."}</div>
                {analyticsDepartment && analyticsDepartment !== department && (
                  <div style={{ marginTop: 4, color: "#dceee1", fontSize: 14 }}>Viewing: {analyticsDepartment}</div>
                )}
                <div style={{ marginTop: 8, color: "#dceee1", fontSize: 14, fontWeight: 600 }}>SY / Term: {activeTermLabel}</div>
              </div>
              <div style={{ minWidth: 230, padding: "16px 20px", background: "#fff", color: "#222", borderRadius: 8 }}>
                <div style={{ marginBottom: 6, color: "#666", fontSize: 13 }}>Today</div>
                <time dateTime={dashboardNow.toISOString()} style={{ fontSize: 16, fontWeight: 600 }}>
                  {dashboardNow.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </time>
                <div style={{ marginTop: 6, color: "#155c2b", fontSize: 22, fontWeight: 700 }}>
                  {dashboardNow.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                </div>
              </div>
            </section>

            <section style={{ marginBottom: 32 }}>
              <h2 style={{ margin: "0 0 16px", fontSize: 21, color: "#202820" }}>Program Analytics</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14 }}>
                {[
                  { label: "Students", value: dashboardStudentsLoading ? "..." : totalStudents, detail: `In ${analyticsDepartment || "this department"}`, color: "#155c2b" },
                  { label: "New Students", value: dashboardStudentsLoading ? "..." : newStudents, detail: "Application category: New Student", color: "#1976d2" },
                  { label: "Pending", value: dashboardStudentsLoading ? "..." : pendingStudents, detail: "Awaiting enrollment approval", color: "#d97706" }
                ].map(metric => (
                  <article key={metric.label} style={{ minWidth: 0, padding: "18px 20px", background: "#fff", border: "1px solid #dedede", borderTop: `4px solid ${metric.color}`, borderRadius: 8 }}>
                    <div style={{ marginBottom: 12, color: "#555", fontSize: 14, fontWeight: 600 }}>{metric.label}</div>
                    <div style={{ color: metric.color, fontSize: 30, fontWeight: 700, lineHeight: 1.1 }}>{metric.value}</div>
                    <div style={{ marginTop: 8, color: "#777", fontSize: 12 }}>{metric.detail}</div>
                  </article>
                ))}
              </div>
            </section>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
              <section style={{ padding: 20, background: "#fff", border: "1px solid #dedede", borderRadius: 8 }}>
                <h3 style={{ margin: "0 0 6px", fontSize: 17, color: "#202820" }}>Students by Year Level</h3>
                <div style={{ marginBottom: 16, color: "#777", fontSize: 12 }}>{activeTermLabel}</div>
                {yearLevelStats.length > 0 ? yearLevelStats.map(([yearLevel, count]) => (
                  <div key={yearLevel} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6, fontSize: 13 }}>
                      <span>{yearLevel}</span>
                      <strong>{dashboardStudentsLoading ? "..." : count}</strong>
                    </div>
                    <div style={{ height: 7, background: "#edf0ed", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${count / maximumYearLevelCount * 100}%`, height: "100%", background: "#155c2b" }} />
                    </div>
                  </div>
                )) : (
                  <div style={{ padding: "16px 0", color: "#777", fontSize: 13 }}>{dashboardStudentsLoading ? "Loading student data..." : "No year-level records for this department yet."}</div>
                )}
              </section>

              <section style={{ padding: 20, background: "#fff", border: "1px solid #dedede", borderRadius: 8 }}>
                <h3 style={{ margin: "0 0 6px", fontSize: 17, color: "#202820" }}>Enrollment Trend</h3>
                <div style={{ marginBottom: 16, color: "#777", fontSize: 12 }}>Students with schedules across semesters</div>
                {enrollmentTrend.length > 0 ? enrollmentTrend.map(term => (
                  <div key={term.termKey} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6, fontSize: 13 }}>
                      <span>{term.termKey.replace("_", " · ")}</span>
                      <strong>{dashboardStudentsLoading ? "..." : term.students}</strong>
                    </div>
                    <div style={{ height: 7, background: "#edf0ed", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${term.students / maximumTrendCount * 100}%`, height: "100%", background: "#1976d2" }} />
                    </div>
                  </div>
                )) : (
                  <div style={{ padding: "16px 0", color: "#777", fontSize: 13 }}>{dashboardStudentsLoading ? "Loading enrollment data..." : "No semester schedule history for this department yet."}</div>
                )}
              </section>

              <section style={{ padding: 20, background: "#fff", border: "1px solid #dedede", borderRadius: 8 }}>
                <h3 style={{ margin: "0 0 6px", fontSize: 17, color: "#202820" }}>Section Distribution</h3>
                <div style={{ marginBottom: 16, color: "#777", fontSize: 12 }}>Published sections and assigned students across all school years and semesters</div>
                {sectionStats.length > 0 ? sectionStats.map(section => (
                  <div key={`${section.schoolYear}-${section.semester}-${section.yearLevel}-${section.section}`} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6, fontSize: 13 }}>
                      <span>{section.yearLevel} · Section {section.section}<small style={{ display: "block", marginTop: 3, color: "#777" }}>AY {section.schoolYear} · {section.semester}</small></span>
                      <strong>{dashboardStudentsLoading || programSchedulesLoading ? "..." : section.students}</strong>
                    </div>
                    <div style={{ height: 7, background: "#edf0ed", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${section.students / maximumSectionCount * 100}%`, height: "100%", background: "#e67e22" }} />
                    </div>
                  </div>
                )) : (
                  <div style={{ padding: "16px 0", color: "#777", fontSize: 13 }}>
                    {dashboardStudentsLoading || programSchedulesLoading
                      ? "Loading scheduled sections..."
                      : "No published sections found in Scheduling Management for this department."}
                  </div>
                )}
              </section>
            </div>

            <section style={{ marginTop: 32 }}>
              <h2 style={{ margin: "0 0 14px", fontSize: 18, color: "#202820" }}>Quick Actions</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                <button onClick={() => setPage("curriculum-flowchart")} style={{ padding: "16px", background: "#155c2b", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: "pointer", transition: "background 0.2s" }} onMouseEnter={e => e.target.style.background = "#0d3d1f"} onMouseLeave={e => e.target.style.background = "#155c2b"}>Curriculum Flowchart</button>
                <button onClick={() => setPage("scheduling")} style={{ padding: "16px", background: "#155c2b", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: "pointer", transition: "background 0.2s" }} onMouseEnter={e => e.target.style.background = "#0d3d1f"} onMouseLeave={e => e.target.style.background = "#155c2b"}>Scheduling</button>
                <button onClick={() => navigate("/program-head/student-course-lineup")} style={{ padding: "16px", background: "#e67e22", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: "pointer", transition: "background 0.2s" }} onMouseEnter={e => e.target.style.background = "#d46a10"} onMouseLeave={e => e.target.style.background = "#e67e22"}>Course Line-up</button>
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  // --- Curriculum Page (extracted) ---
  if (page === "curriculum-flowchart") {
    return (
      <div className="program-head-layout" style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
        {/* Sidebar */}
        <div className="program-head-sidebar" style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
          <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
          
          <button onClick={() => setPage("menu")} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Home</button>
          <button onClick={() => setPage("curriculum-flowchart")} style={{ background: page === "curriculum-flowchart" ? "#e67e22" : "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s", boxShadow: page === "curriculum-flowchart" ? "0 2px 8px #e67e22aa" : "none" }}>Curriculum Flowchart</button>
          <button onClick={() => setPage("scheduling")} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Scheduling</button>
          <button onClick={() => navigate("/program-head/student-course-lineup")} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Course Line-up</button>
          <div style={{ flex: 1 }} />
        </div>

        {/* Main area */}
        <div className="program-head-main" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          {/* Header */}
          <div className="program-head-header" style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "relative", zIndex: 10, justifyContent: "space-between" }}>
            <h1 style={{ margin: "0", fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Curriculum Management</h1>
            
            <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
              <span style={{ marginRight: 16, fontWeight: 600, fontSize: 18, color: "#155c2b" }}>{phName || "Program Head"}</span>
              <button onClick={() => setDropdownOpen(!dropdownOpen)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                <img src={profilePicUrl} alt="Profile" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e67e22" }} />
              </button>
              
              {dropdownOpen && (
                <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 20, minWidth: 200, overflow: "hidden" }}>
                  <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>{phName || "Program Head"}</div>
                      <button
                        onClick={() => { setDropdownOpen(false); setShowResetModal(true); }}
                        style={{ width: "100%", padding: "12px 16px", background: "transparent", border: "none", color: "#155c2b", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", transition: "background 0.2s" }}
                        onMouseEnter={(e) => e.target.style.background = "#f5fff0"}
                        onMouseLeave={(e) => e.target.style.background = "transparent"}
                      >
                        Change Password
                      </button>
                      <button onClick={() => { setDropdownOpen(false); handleLogout(); }} style={{ width: "100%", padding: "12px 16px", background: "transparent", border: "none", color: "#e67e22", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", transition: "background 0.2s" }} onMouseEnter={(e) => e.target.style.background = "#fef5f0"} onMouseLeave={(e) => e.target.style.background = "transparent"}>Log out</button>
                      {showResetModal && <ResetPassword email={phEmail} onClose={() => setShowResetModal(false)} />}
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="program-head-content" style={{ flex: 1, padding: 40, overflowY: "auto" }}>
            <HeadCurriculum
              department={department}
              selectedDepartment={selectedDepartment}
              setSelectedDepartment={setSelectedDepartment}
              allDepartments={allDepartments}
              onBack={() => setPage("menu")}
              initialShowFlowchart={page === "curriculum-flowchart"}
            />
          </div>
        </div>
      </div>
    );
  }

  // --- Scheduling Page ---
  const isGeUser = (department || '').toLowerCase().includes('general');

  if (page === "scheduling") {
    return (
      <div className="program-head-layout" style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
        {/* Sidebar */}
        <div className="program-head-sidebar" style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
          <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
          
          <button onClick={() => setPage("menu")} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Home</button>
          <button onClick={() => setPage("curriculum-flowchart")} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Curriculum Flowchart</button>
          <button onClick={() => setPage("scheduling")} style={{ background: "#e67e22", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, boxShadow: "0 2px 8px #e67e22aa" }}>Scheduling</button>
          <button onClick={() => navigate("/program-head/student-course-lineup")} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Course Line-up</button>
          <div style={{ flex: 1 }} />
        </div>

        {/* Main area */}
        <div className="program-head-main" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          {/* Header */}
          <div className="program-head-header" style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "relative", zIndex: 10, justifyContent: "space-between" }}>
            <h1 style={{ margin: "0", fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Scheduling</h1>
            
            <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
              <span style={{ marginRight: 16, fontWeight: 600, fontSize: 18, color: "#155c2b" }}>{phName || "Program Head"}</span>
              <button onClick={() => setDropdownOpen(!dropdownOpen)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                <img src={profilePicUrl} alt="Profile" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e67e22" }} />
              </button>
              
              {dropdownOpen && (
                <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 20, minWidth: 200, overflow: "hidden" }}>
                  <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>{phName || "Program Head"}</div>
                      <button
                        onClick={() => { setDropdownOpen(false); setShowResetModal(true); }}
                        style={{ width: "100%", padding: "12px 16px", background: "transparent", border: "none", color: "#155c2b", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", transition: "background 0.2s" }}
                        onMouseEnter={(e) => e.target.style.background = "#f5fff0"}
                        onMouseLeave={(e) => e.target.style.background = "transparent"}
                      >
                        Change Password
                      </button>
                      <button onClick={() => { setDropdownOpen(false); handleLogout(); }} style={{ width: "100%", padding: "12px 16px", background: "transparent", border: "none", color: "#e67e22", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", transition: "background 0.2s" }} onMouseEnter={(e) => e.target.style.background = "#fef5f0"} onMouseLeave={(e) => e.target.style.background = "transparent"}>Log out</button>
                      {showResetModal && <ResetPassword email={phEmail} onClose={() => setShowResetModal(false)} />}
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="program-head-content" style={{ flex: 1, padding: 40, overflowY: "auto" }}>
            {isGeUser ? (
              <GESchedulingManagement
                department={department}
                selectedDepartment={selectedDepartment}
                setSelectedDepartment={setSelectedDepartment}
                allDepartments={allDepartments}
                onBack={() => setPage("menu")}
              />
            ) : (
              <SchedulingManagement
                department={department}
                selectedDepartment={selectedDepartment}
                setSelectedDepartment={setSelectedDepartment}
                allDepartments={allDepartments}
                onBack={() => setPage("menu")}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ProgramHeadDashboard;
